// Shared response/domain types for the console JSON API (services/v2/ui/server.ts's
// `/api/v2/*` routes). Each screen feature owns the endpoints/types it needs —
// add to this file rather than duplicating a type inline in a page component.

export interface AccountFilterOption {
  id: string;
  name: string;
}

export interface AccountFilterState {
  options: AccountFilterOption[];
  selected: string | null;
}

// GET /api/v2/me. email/name are only populated under CONSOLE_AUTH_MODE=workos
// — every other auth mode has no real per-user identity to report, so both
// come back null there rather than the route 404ing.
export interface MeResponse {
  who: string;
  role: string | null;
  email: string | null;
  name: string | null;
}

// The only 7 values Meeting Status accepts (operator instruction, 2026-08-20)
// — mirrors MEETING_STATUS_OPTIONS in services/v2/queue.ts exactly (the
// single source of truth there; duplicated here because there is no shared
// JSON endpoint that hands this list over — see GET /api/v2/leads's own
// comment). Keep the two in sync by hand if that list ever changes.
export const MEETING_STATUS_OPTIONS = [
  "Done", "To be Scheduled", "Scheduled", "Given us Contact No.",
  "Didn't join the call", "Rescheduled", "POC before Call",
] as const;
export type MeetingStatus = typeof MEETING_STATUS_OPTIONS[number];

// Mirrors GET /api/v2/leads's response shape (services/v2/ui/server.ts),
// itself a JSON-camelCased projection of queue.ts's ConversationRow — one row
// per CONVERSATION (not per lead; a lead with no conversation yet has no row
// here), same as the HTML /all screen's table.
export interface Lead {
  // The writable key setMeetingStatus/setFollowUpDate/setMeetingDate
  // (queue.ts) take — an ops.leads.id (UUID), NOT `publicId` below. null for
  // the real, documented case of a conversation with no ops.leads row yet
  // (allConversations' own LEFT JOIN comment, queue.ts) — the per-row editors
  // must be disabled in that case, same as the HTML table's meetingStatusCell/
  // followUpCell/meetingDateCell do.
  id: string | null;
  // The conversation's console id — links to /c/:id (conversation detail).
  publicId: string;
  leadKey: string;
  name: string | null;
  company: string | null;
  accountId: string;
  accountName: string | null;
  campaignId: string;
  campaignName: string | null;
  // Drives the "positive"/"responded"/"all" tabs — "positive" tab shows rows
  // where this is exactly "positive"; "responded" shows any row with a
  // non-null lastReceivedAt, independent of classification.
  classification: string | null;
  meetingStatus: MeetingStatus | null;
  followUp: string | null; // "YYYY-MM-DD" or null
  meetingDate: string | null; // "YYYY-MM-DD" or null
  lastMessageAt: string | null; // ISO 8601
  lastReceivedAt: string | null; // ISO 8601
  lastSentAt: string | null; // ISO 8601
  pendingCount: number;
  hubspotContactId: string | null;
  hubspotDealId: string | null;
  dealstage: string | null;
  dismissedAt: string | null; // ISO 8601
  handedOverAt: string | null; // ISO 8601
}

// POST /api/v2/leads/:id/{meeting-status,follow-up,meeting-date}'s response —
// same {ok, message} shape the equivalent form-POST routes already return for
// an `Accept: application/json` request (server.ts).
export interface LeadFieldWriteResult {
  ok: boolean;
  message: string;
}

// ── Inbox (feature/inbox, `/`) ──────────────────────────────────────────────
// Mirrors GET /api/v2/inbox's response shape, which is services/v2/queue.ts's
// QueueRow/DismissedRow serialized as JSON (services/v2/ui/server.ts). Kept
// snake_case / close to the server's own field names, same convention
// ConversationDetailConv below already uses, so a diff against queue.ts's own
// types stays easy.

export type QueueReason = "drafting" | "draft_pending" | "draft_missing" | "send_failed";

export interface QueueRow {
  reply_id: string | null;
  draft: string;
  edited_text: string | null;
  state: string;
  lead_key: string | null;
  public_id: string | null;
  lead_id: string;
  lead_name: string | null;
  company: string | null;
  classification: string | null;
  reply_summary: string | null;
  handover_trigger: boolean;
  lead_status: string;
  campaign_name: string | null;
  campaign_id: string | null;
  origin_id: string | null;
  origin_name: string | null;
  origin_kind: string | null;
  linkedin_account_id: string | null;
  account_name: string | null;
  linkedin_url: string | null;
  hubspot_contact_id: string | null;
  hubspot_deal_id: string | null;
  dealstage: string | null;
  last_inbound: string | null;
  last_message_at: string | null; // ISO 8601
  attribution_exact: boolean;
  lead_picture_url: string | null;
  queue_reason: QueueReason;
}

export interface DismissedRow {
  public_id: string;
  lead_name: string | null;
  company: string | null;
  classification: string | null;
  last_message_at: string | null; // ISO 8601
  last_inbound: string | null;
  dismissed_at: string; // ISO 8601
  dismissed_by: string | null;
}

export interface InboxData {
  rows: QueueRow[];
  dismissed: DismissedRow[];
  // Counts for all three priority tabs (Priority/Other/All), computed
  // server-side against the SAME non-priority filters (campaign/class/
  // account/etc.) already applied to `rows` — so switching tabs shows a real
  // count immediately instead of only the currently-selected one. Optional
  // only so an older cached response (or the mock server, before it adds
  // this) degrades to today's "only the active tab has a count" behavior
  // rather than crashing.
  counts?: { priority: number; other: number; all: number };
}

// Inbox-only triage toggle (services/v2/queue.ts's Filters.priority) — mirrors
// the same 3-way "priority"/"other"/"all" convention the HTML route's tabs use.
export type PriorityFilter = "priority" | "other";

export interface InboxFilters {
  class?: string;
  campaign?: string;
  q?: string;
  handover?: boolean;
  state?: string;
  priority?: PriorityFilter;
}

export interface LiveState {
  digest: string;
  notify: string | null;
}

// Placeholder shape kept for whatever else ends up wanting a minimal
// conversation reference (e.g. a row link) — the Inbox screen itself uses
// QueueRow/DismissedRow above, and the Conversation Detail screen uses
// ConversationDetail* below; neither needs this generic shape today.
export interface Conversation {
  id: string;
  leadName: string;
  campaignId: string;
  dismissedAt: string | null;
  dismissedBy: string | null;
}

// ── Conversation Detail (feature/conversation-detail, `/c/:id`) ────────────
// Mirrors GET /api/v2/c/:id's response shape, which is
// services/v2/ui/server.ts's loadDetailBundle() (== ConversationScreenInput
// in services/v2/ui/views.ts) serialized as JSON rather than rendered through
// conversationScreen(). Kept close to the server's own field names
// (snake_case in `conv`/`replies`) rather than remapped to camelCase, so a
// diff against services/v2/queue.ts's ConversationRow/ConversationDetail
// types stays easy — this is a read view, not a place to invent a second
// vocabulary for the same data.

export type ThreadMessageDirection = "inbound" | "outbound";

export interface ThreadMessage {
  urn: string | null;
  direction: ThreadMessageDirection;
  body: string;
  at: string; // ISO 8601
}

export interface CachedThread {
  source: "aimfox" | "local-partial";
  messages: ThreadMessage[];
  degradedReason: string | null;
  possiblyTruncated: boolean;
  fetchedAt: string | null;
  fromCache: boolean;
  degradedKind?: "budget_exhausted";
}

// PLAN-CAMPAIGN-ACCEPT.md Phase 3 (2026-08-27, merged to main — see
// services/v2/pending-draft.ts there): a draft "group" is up to 3 variants,
// not one row. variantIndex 1 is the workflow-authoritative one (the only one
// a Slack revision can go stale against); 2/3 read straight off their own DB
// row (source "db"). "db-fallback" is variantIndex 1 specifically, when the
// workflow couldn't be queried.
export interface DraftVariant {
  variantIndex: 1 | 2 | 3;
  aiReplyId: string;
  text: string;
  source: "workflow" | "db" | "db-fallback";
  revisedBy: string | null;
}

export interface PendingDraft {
  variants: DraftVariant[];
  // Set only when variantIndex 1's workflow query degraded to the DB
  // fallback — 2/3 never produce a reason (see DraftVariant's own comment).
  reason: string | null;
  alreadyDecided: { action?: string; approver?: string } | null;
}

export interface DecideGate {
  allowed: boolean;
  reason: string;
  allowlist: string;
}

// aiReplyId dropped from here (PLAN-CAMPAIGN-ACCEPT.md Phase 3) — each
// PendingDraft.variants entry now carries its own; the decide POST takes
// whichever variant's tab the operator is acting on.
export interface DecideState {
  gate: DecideGate;
  approver: string;
  flash?: { kind: "ok" | "error"; message: string };
}

export interface ConversationDetailReply {
  id: string;
  draft: string;
  edited_text: string | null;
  state: string;
  approver: string | null;
  decided_at: string | null;
  workflow_id: string | null;
}

// The full ConversationRow fields conversationDetail() (services/v2/queue.ts)
// joins for Screen B, plus the detail-only extensions (account_sendable,
// picture URLs, conversation_urn, iris_link, title). Dates arrive as ISO
// strings — JSON.stringify serialized the server's Date objects.
export interface ConversationDetailConv {
  lead_key: string;
  public_id: string;
  lead_id: string;
  lead_name: string | null;
  company: string | null;
  campaign_id: string;
  campaign_name: string | null;
  origin_id: string | null;
  origin_name: string | null;
  origin_kind: string | null;
  linkedin_account_id: string;
  linkedin_url: string;
  conversation_urn: string | null;
  classification: string | null;
  lead_status: string;
  handed_over_at: string | null;
  handover_reason: string | null;
  hubspot_contact_id: string | null;
  hubspot_deal_id: string | null;
  dealstage: string | null;
  last_message_at: string | null;
  last_received_at: string | null;
  last_sent_at: string | null;
  pending_count: number;
  iris_link: string | null;
  iris_link_synced_at: string | null;
  dismissed_at: string | null;
  dismissed_by: string | null;
  account_name: string | null;
  account_sendable: boolean;
  account_picture_url: string | null;
  lead_picture_url: string | null;
  meeting_status: string | null;
  follow_up_date: string | null;
  meeting_date: string | null;
  title: string | null;
}

export interface ConversationDetailData {
  conv: ConversationDetailConv;
  replies: ConversationDetailReply[];
  aiSummary: string | null;
  replyReasoning: string | null;
  latestReplyTag: string | null;
  handoverReason: string | null;
  prospectProfile: ProspectProfile | null;
  followups: { sequence: number; due_at: string | null; state: string }[];
}

export interface ProspectProfile {
  full_name: string | null;
  // Verbatim LinkedIn headline and connection degree — a compact identity
  // snapshot shown above the researched signals. connection_degree is
  // usually null (see enrich-lead-prospect's own SKILL.md comment: it isn't
  // visible to a general web search).
  headline: string | null;
  connection_degree: string | null;
  current_title: string | null;
  current_company: string | null;
  company_industry: string | null;
  company_size_stage: string | null;
  location: string | null;
  career_history_summary: string | null;
  education: string | null;
  recent_activity_summary: string | null;
  achievements: string | null;
  pain_points_signals: string | null;
  mutual_signals: { summary: string | null; confidence: "low" | null };
  citations: { url: string; note: string | null }[];
  researchFailed: boolean;
}

export interface ConversationDetail {
  detail: ConversationDetailData;
  thread: CachedThread;
  pending: PendingDraft;
  portal: { portalId: string; uiDomain: string } | null;
  decide?: DecideState;
}

export type DecideIntent = "send";

// ── Campaigns (feature/campaigns, `/campaigns` + `/campaigns/:id`) ────────
// Mirrors services/v2/campaigns.ts's own types field-for-field (snake_case
// kept, same reasoning as ConversationDetailConv above: this is a read view
// over services/v2/ui/campaign-views.ts's data, not a place to invent a
// second vocabulary) plus GET /api/v2/campaigns and GET /api/v2/campaigns/:id's
// actual response envelopes (services/v2/ui/server.ts's
// buildCampaignsListData()/buildCampaignDetailData()).

export interface AimfoxCampaign {
  id: string;
  name: string;
  state: string;
  outreach_type: string | null;
  owners: string[];
  target_count: number | null;
  created_at: number | null;
}

export interface AimfoxMetrics {
  sent_connections?: number;
  accepted_connections?: number;
  sent_messages?: number;
  replies?: number;
  inmail_replies?: number;
  sent_inmails?: number;
  message_requests?: number;
  sent_endorsements?: number;
  views?: number;
  sent_likes?: number;
}

export interface Snapshot {
  campaign_id: string;
  snapshot_date: string;
  aimfox_metrics: AimfoxMetrics | null;
  ai_metrics: AiMetrics | null;
  capture_error: string | null;
  captured_at: string;
  source?: string | null;
}

export interface AiMetrics {
  replies_received: number;
  accepted_events: number;
  conversations: number;
  positive_leads: number;
  drafts_total: number;
  drafts_pending: number;
  needs_draft: number;
  drafts_sent: number;
  drafts_rejected: number;
  drafts_superseded: number;
  deals: number;
  deals_by_stage: Record<string, number>;
  handovers: number;
  classification_mix: Record<string, number>;
  origin_label: string | null;
  origin_kind: string | null;
}

export interface CampaignRow {
  campaign: AimfoxCampaign;
  snapshot: Snapshot | null;
  ai: AiMetrics | null;
  // ops.campaigns.first_touch_enabled — labeled "Connection-only" in the UI.
  // See CampaignFirstTouchToggle for where it's set.
  firstTouchEnabled: boolean;
}

export interface CampaignsListResponse {
  rows: CampaignRow[];
  // Keyed by campaign id — up to 30 days, for the reply-trend sparkline.
  history: Record<string, Snapshot[]>;
  listError: string | null;
  disconnectedAccountSelected: boolean;
}

// The lightweight GET /api/v2/campaigns-picker — {id, name, state} only, one
// live Aimfox call and nothing else (no snapshot history, no AI metrics).
// Use this for a picker/dropdown; use CampaignsListResponse only for the
// actual Campaigns dashboard screen, which needs the rest of that data.
export interface CampaignsPickerResponse {
  campaigns: AimfoxCampaign[];
  listError: string | null;
}

export interface CampaignDetailResponse {
  campaign: AimfoxCampaign;
  snapshots: Snapshot[];
  ai: AiMetrics | null;
  objective: string | null;
  firstTouchEnabled: boolean;
}

export interface CampaignObjectiveResponse {
  id: string;
  name: string;
  objective: string | null;
}

export interface CampaignFirstTouchEnabledResponse {
  id: string;
  name: string;
  firstTouchEnabled: boolean;
}

// ── Settings: per-campaign email notification recipients ────────────────────
// Backend: services/v2/notification-settings.ts, ops_v2.notification_recipients
// + ops_v2.notification_campaign_prefs (services/v2/db/schema-v2.sql).

export const MESSAGE_TYPES = ["positive", "negative", "neutral", "all"] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

// Sentinel campaignId for the settings form's "All campaigns" option — mirrors
// the same constant in services/v2/notification-settings.ts. Kept as its own
// row (not a boolean) so a recipient can combine an "All campaigns" pref with
// a specific-campaign override; see that file's comment for the full reasoning.
export const ALL_CAMPAIGNS = "ALL";

export interface CampaignNotificationPref {
  campaignId: string;
  messageTypes: MessageType[];
}

export interface NotificationRecipient {
  id: string;
  email: string;
  campaignPrefs: CampaignNotificationPref[];
  createdAt: string;
  updatedAt: string;
}

export interface NotificationSettingsListResponse {
  recipients: NotificationRecipient[];
}

export interface NotificationRecipientResponse {
  recipient: NotificationRecipient;
}

// Kept as the minimal shared shape a few other screens' placeholders may
// still reference — the real data now lives in the types above.
export interface Campaign {
  id: string;
  name: string;
  objective: string | null;
}

export interface ApiError {
  error: string;
}
